/**
 * Covers the app while it works out whether another Portal has signed this browser in since.
 *
 * Picking that up costs a top-level round trip: this site cannot read the broker's cookie, so it
 * has to ask, and asking means navigating away and coming back. The trip itself is short --
 * measured at 0.6-1.3s on DEV -- but it was started from an effect, which runs *after* the browser
 * has painted. So the business screen appeared in full, then was torn down and rebuilt. Two paints
 * for one arrival reads as the page glitching, and no amount of speed fixes that impression; only
 * not showing the first paint does.
 *
 * The curtain therefore goes up before React renders anything, and comes down only when the answer
 * is "nothing to do". When the answer is "there is a newer session", the navigation happens behind
 * it and the user sees one continuous load instead of a flash.
 *
 * It is deliberately plain DOM with inline styles: it has to be painted before React exists and
 * survive the teardown of everything React owns, and it cannot assume any stylesheet has loaded.
 */

const ELEMENT_ID = 'sso-sync-curtain';

/** Nothing may leave the user staring at a curtain, so it always has a way down. */
const SAFETY_TIMEOUT_MS = 8000;

let safetyTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Paths that render OUTSIDE ProtectedRoute / ProtectedSiteAdminRoute.
 *
 * The curtain is raised from module scope in main.tsx and is only ever taken down by
 * useActiveSessionSync, which mounts inside those guards. On a route without a guard nothing
 * lowers it, so the page sits behind a white overlay until SAFETY_TIMEOUT_MS — 8 seconds during
 * which no request is even in flight. `rezepts/pdf-progress` is the print popup and the only such
 * route that stays on screen; the other guardless routes (login, password reset, sso/complete)
 * either navigate away immediately or have no stored token, so the curtain never goes up there.
 * (HIT-11388)
 */
const CURTAINLESS_PATH_PATTERNS: readonly RegExp[] = [
  /^\/[^/]+\/rezepts\/pdf-progress\/?$/,
];

/** True when this path has no session-sync guard to take the curtain back down. */
export const isCurtainlessPath = (pathname: string): boolean =>
  CURTAINLESS_PATH_PATTERNS.some(pattern => pattern.test(pathname));

export const showSsoSyncCurtain = (
  message = 'ログイン状態を確認しています…',
): void => {
  if (typeof document === 'undefined' || document.getElementById(ELEMENT_ID)) {
    return;
  }

  const curtain = document.createElement('div');
  curtain.id = ELEMENT_ID;
  curtain.setAttribute('role', 'status');
  curtain.setAttribute('aria-live', 'polite');
  curtain.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:16px',
    'background:#ffffff',
    'color:#4b5563',
    'font-family:system-ui,-apple-system,"Segoe UI","Hiragino Sans","Noto Sans JP",sans-serif',
    'font-size:14px',
  ].join(';');

  const style = document.createElement('style');
  style.textContent =
    '@keyframes sso-sync-curtain-spin{to{transform:rotate(360deg)}}';

  const spinner = document.createElement('div');
  spinner.style.cssText = [
    'width:36px',
    'height:36px',
    'border-radius:50%',
    'border:3px solid rgba(0,0,0,0.12)',
    'border-top-color:#4b5563',
    'animation:sso-sync-curtain-spin 0.8s linear infinite',
  ].join(';');

  const label = document.createElement('div');
  label.textContent = message;

  curtain.append(style, spinner, label);
  document.body.append(curtain);

  if (safetyTimer !== undefined) {
    clearTimeout(safetyTimer);
  }
  safetyTimer = setTimeout(hideSsoSyncCurtain, SAFETY_TIMEOUT_MS);
};

export const hideSsoSyncCurtain = (): void => {
  if (safetyTimer !== undefined) {
    clearTimeout(safetyTimer);
    safetyTimer = undefined;
  }
  if (typeof document === 'undefined') {
    return;
  }
  document.getElementById(ELEMENT_ID)?.remove();
};

/** True when the user reloaded this page themselves, rather than arriving by navigation. */
export const isUserPageReload = (): boolean =>
  (
    performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
  )?.type === 'reload';
